import { useEffect, useState } from 'react';

/**
 * Tema claro/oscuro. Aplica data-theme en <html>; los tokens del design system
 * (styles.css) tienen su variante en :root[data-theme="dark"]. El valor inicial
 * lo setea un script inline en index.html para evitar el flash.
 */
export type Theme = 'light' | 'dark';
const KEY = 'theme';

/**
 * LAS PÁGINAS PÚBLICAS SON SIEMPRE CLARAS.
 *
 * Reporte de Manuel (2026-09-04): "cuando la gente está llenando el intake form salen los campos
 * para poner información oscuros… cuando escriben no se ve nada".
 *
 * MEDIDO contra prod: el formulario público corre con `data-theme="dark"`, porque el tema por
 * defecto del app es OSCURO y un homeowner que abre el link por primera vez no tiene nada guardado
 * en localStorage. Casi todos los campos del form se salvaban por tener su color en línea; el de
 * DIRECCIÓN no, porque es el único que usa el `AddressAutocomplete` del app con `className="input"`
 * — y salía como una caja negra en medio de una hoja blanca, con el desplegable de sugerencias
 * también negro.
 *
 * Parchear ese campo habría tapado el síntoma. La causa es que un documento que se le manda a un
 * cliente heredaba el tema del STAFF, y lo mismo aplica a la página de FIRMAR EL CONTRATO. Se
 * arregla en la raíz: en estas rutas el tema es claro, y cualquier componente del design system
 * que alguien agregue mañana sale bien sin acordarse de nada.
 *
 * OJO: esta lista está DUPLICADA en el script sin-flash de `index.html` (no puede importar de acá).
 * `tests/tema-publico.test.ts` compara las dos y falla si se separan.
 */
/**
 * `/portal/` está acá por si el Client Portal cae en el SPA del staff: el portal tiene su propia
 * entrada de Vite con `data-theme="light"` fijo (frontend/portal/index.html), pero cuando
 * PORTAL_ENABLED está apagado el backend deja pasar `/portal/*` al catch-all y lo atiende esta app.
 * Sin la ruta en la lista, esa pantalla saldría oscura.
 */
export const RUTAS_PUBLICAS = ['/intake/', '/sign/', '/p/', '/portal/'];
export const esRutaPublica = (path: string): boolean => RUTAS_PUBLICAS.some((p) => path.startsWith(p));

export function getTheme(): Theme {
  const attr = document.documentElement.getAttribute('data-theme');
  return attr === 'dark' ? 'dark' : 'light';
}

export function setTheme(theme: Theme): void {
  document.documentElement.setAttribute('data-theme', theme);
  try {
    localStorage.setItem(KEY, theme);
  } catch {
    /* almacenamiento no disponible: se queda solo en memoria */
  }
  window.dispatchEvent(new CustomEvent('themechange', { detail: theme }));
}

/** Hook para el toggle: devuelve el tema actual y una función para alternarlo. */
export function useTheme(): { theme: Theme; toggle: () => void } {
  const [theme, setThemeState] = useState<Theme>(getTheme);
  useEffect(() => {
    const onChange = () => setThemeState(getTheme());
    window.addEventListener('themechange', onChange);
    return () => window.removeEventListener('themechange', onChange);
  }, []);
  return { theme, toggle: () => setTheme(theme === 'dark' ? 'light' : 'dark') };
}

/**
 * Fuerza el tema CLARO mientras la página pública esté montada, y devuelve el anterior al salir.
 * El script de `index.html` ya lo resuelve en la primera carga (sin flash); esto cubre la
 * navegación dentro de la SPA y el caso de alguien del staff que abre el link con el app abierto.
 */
export function useTemaClaroPublico(): void {
  useEffect(() => {
    const antes = document.documentElement.getAttribute('data-theme');
    document.documentElement.setAttribute('data-theme', 'light');
    return () => {
      if (antes) document.documentElement.setAttribute('data-theme', antes);
    };
  }, []);
}
